import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'react-hot-toast'
import { usePopup } from '@/contexts/PopupContext'

interface QRCodePopupProps {
  isOpen: boolean
  onClose: () => void
}

export const QRCodePopup: React.FC<QRCodePopupProps> = ({ isOpen, onClose }) => {
  const { openPopup, closePopup } = usePopup()
  const prevIsOpen = React.useRef(isOpen)
  React.useEffect(() => {
    if (!prevIsOpen.current && isOpen) {
      openPopup()
    }
    if (prevIsOpen.current && !isOpen) {
      closePopup()
    }
    prevIsOpen.current = isOpen
  }, [isOpen])

  const qrValue = 'https://pango.com/staff/ngoclan' // Giả sử đây là link của nhân viên
  const staffName = 'Ngọc Lan'
  const staffRole = 'Nhân viên bán hàng - CH Quận 3'

  const handleDownload = async () => {
    const svg = document.querySelector('#staff-qr-code svg') as SVGElement
    if (!svg) return

    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Tạo một image từ SVG
      const svgData = new XMLSerializer().serializeToString(svg)
      const img = new Image()
      
      img.onload = () => {
        // Set kích thước canvas lớn hơn để chứa cả avatar
        canvas.width = img.width + 60 // Thêm padding
        canvas.height = img.height + 60
        
        // Vẽ background trắng
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        
        // Vẽ QR code ở giữa
        ctx.drawImage(img, 30, 30)
        
        // Vẽ avatar ở giữa QR code
        ctx.beginPath()
        ctx.arc(canvas.width/2, canvas.height/2, 20, 0, Math.PI * 2)
        ctx.fillStyle = '#3b82f6'
        ctx.fill()
        ctx.fillStyle = 'white'
        ctx.font = 'bold 24px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('NL', canvas.width/2, canvas.height/2)

        // Tải xuống
        const link = document.createElement('a')
        link.download = `QR-${staffName}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
        toast.success('Đã tải mã QR')
      }

      img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
    } catch (error) {
      console.error('Error downloading QR code:', error)
      toast.error('Không thể tải mã QR')
    }
  }

  const handleShare = async () => {
    try {
      const svg = document.querySelector('#staff-qr-code svg') as SVGElement
      if (!svg) return

      const svgData = new XMLSerializer().serializeToString(svg)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const img = new Image()
      img.onload = async () => {
        canvas.width = img.width + 60
        canvas.height = img.height + 60
        
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 30, 30)
        
        try {
          const blob = await new Promise<Blob>((resolve) => canvas.toBlob(resolve as BlobCallback, 'image/png'))
          const file = new File([blob], `QR-${staffName}.png`, { type: 'image/png' })
          
          if (navigator.share) {
            await navigator.share({
              title: `Mã QR của ${staffName}`,
              text: `Quét mã QR để kết nối với ${staffName} nhé!`,
              files: [file]
            })
            toast.success('Đã chia sẻ mã QR')
          }
        } catch (error) {
          console.error('Error sharing:', error)
          toast.error('Không thể chia sẻ mã QR')
        }
      }

      img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
    } catch (error) {
      console.error('Error sharing QR code:', error)
      toast.error('Không thể chia sẻ mã QR')
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Popup */}
      <div className="fixed p-6 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-sm bg-white rounded-2xl shadow-2xl z-50">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200"
        >
          <XMarkIcon className="w-5 h-5 text-gray-500" />
        </button>

        {/* Title */}
        <h2 className="text-lg font-semibold text-gray-900 mb-6">
          Mã QR của {staffName}
        </h2>

        {/* QR Code */}
        <div className="flex justify-center">
          <div 
            id="staff-qr-code"
            className="relative bg-white p-4 rounded-3xl shadow-lg"
          >
            <QRCodeSVG
              value={qrValue}
              size={200}
              level="H"
              includeMargin={true}
              imageSettings={{
                src: '',
                height: 40,
                width: 40,
                excavate: true
              }}
            />
            <div 
              className="absolute left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%]
                        w-[40px] h-[40px] bg-blue-500 rounded-full flex items-center justify-center
                        ring-4 ring-white"
            >
              <span className="text-white text-sm font-bold">NL</span>
            </div>
          </div>
        </div>

        {/* Staff info */}
        <div className="mt-6 text-center">
          <p className="text-gray-600">{staffRole}</p>
          <p className="text-sm text-gray-500 mt-1">
            Quét mã QR để kết nối với {staffName} nhé!
          </p>
        </div>

        {/* Actions */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={handleDownload}
            className="flex items-center justify-center gap-2 px-4 py-2.5 
                     bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                     rounded-xl text-gray-700 font-medium transition-colors duration-200"
          >
            Tải xuống
          </button>
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-2 px-4 py-2.5
                     bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                     rounded-xl text-white font-medium transition-colors duration-200"
          >
            Chia sẻ
          </button>
        </div>
      </div>
    </>
  )
} 